import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import {
  ConnectedPosition,
  Overlay,
  ScrollStrategy,
} from "@angular/cdk/overlay";
import { createBlockedOverlayScrollStrategy } from "../../../utils/overlay-scroll-strategy.util";
import { CoreDropdownOption } from "../dropdown/dropdown.type";

export type CoreUiCompactSelectAppearance = "dark" | "light";
export type CoreUiCompactSelectSize = "sm" | "md";
export type CoreUiCompactSelectOptionIndicatorPosition = "left" | "right";

@Component({
  selector: "core-ui-compact-select",
  templateUrl: "./compact-select.component.html",
  styleUrls: ["./compact-select.component.scss"],
  standalone: false,
})
export class CompactSelectComponent {
  @ViewChild("trigger") private trigger?: ElementRef<HTMLButtonElement>;
  @ViewChild("panelSurface") private panelSurface?: ElementRef<HTMLElement>;
  @ViewChildren("optionButton") private optionButtons?: QueryList<ElementRef<HTMLButtonElement>>;

  @Input() options: CoreDropdownOption[] = [];
  @Input() value = "";
  @Input() placeholder = "Seleccionar";
  @Input() disabled = false;
  @Input() appearance: CoreUiCompactSelectAppearance = "dark";
  @Input() panelAppearance?: CoreUiCompactSelectAppearance;
  @Input() size: CoreUiCompactSelectSize = "md";
  @Input() optionIndicatorPosition: CoreUiCompactSelectOptionIndicatorPosition = "left";
  @Input()
  set panelGap(value: number) {
    const nextGap = Number(value);
    this._panelGap = Number.isFinite(nextGap) ? Math.max(0, nextGap) : 12;
    this.positions = this.createPositions(this._panelGap);
  }

  get panelGap(): number {
    return this._panelGap;
  }

  @Output() valueChange = new EventEmitter<string>();

  readonly scrollStrategy: ScrollStrategy;
  positions: ConnectedPosition[];

  isOpen = false;
  private _panelGap = 12;

  constructor(overlay: Overlay) {
    this.scrollStrategy = createBlockedOverlayScrollStrategy(overlay);
    this.positions = this.createPositions(this._panelGap);
  }

  get displayText(): string {
    const option = this.options.find(
      (item) => this.optionValue(item) === this.value,
    );
    return option ? this.optionLabel(option) : this.placeholder;
  }

  get resolvedPanelAppearance(): CoreUiCompactSelectAppearance {
    return this.panelAppearance ?? this.appearance;
  }

  toggle(): void {
    if (this.disabled || this.options.length === 0) return;
    if (this.isOpen) {
      this.close();
      return;
    }

    this.isOpen = true;
  }

  close(restoreFocus = false): void {
    this.isOpen = false;
    if (restoreFocus) queueMicrotask(() => this.trigger?.nativeElement.focus());
  }

  select(option: CoreDropdownOption): void {
    if (this.disabled) return;
    const value = this.optionValue(option);
    this.valueChange.emit(value);
    this.close(true);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape" && this.isOpen) {
      event.preventDefault();
      this.close(true);
      return;
    }

    if ((event.key === "Enter" || event.key === " ") && !this.isOpen) {
      event.preventDefault();
      this.toggle();
    }
  }

  isSelected(option: CoreDropdownOption): boolean {
    return this.optionValue(option) === this.value;
  }

  onPanelAttach(): void {
    queueMicrotask(() => this.panelSurface?.nativeElement.focus());
  }

  onPanelKeydown(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.close(true);
      return;
    }

    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;

    event.preventDefault();
    const buttons = this.optionButtonElements();
    const target = event.key === "ArrowUp" || event.key === "End" ? buttons.length - 1 : 0;
    buttons[target]?.focus();
  }

  onOptionKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close(true);
      return;
    }

    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;

    event.preventDefault();
    const buttons = this.optionButtonElements();
    const current = buttons.indexOf(this.optionButtons?.get(index)?.nativeElement as HTMLButtonElement);
    const target = event.key === "Home"
      ? 0
      : event.key === "End"
        ? buttons.length - 1
        : (current + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length;
    buttons[target]?.focus();
  }

  optionValue(option: CoreDropdownOption): string {
    return typeof option === "string" ? option : option.value;
  }

  optionLabel(option: CoreDropdownOption): string {
    return typeof option === "string" ? option : option.label;
  }

  private optionButtonElements(): HTMLButtonElement[] {
    return (this.optionButtons?.toArray() ?? []).map((item) => item.nativeElement);
  }

  private createPositions(gap: number): ConnectedPosition[] {
    return [
      {
        originX: "start",
        originY: "bottom",
        overlayX: "start",
        overlayY: "top",
        offsetY: gap,
      },
      {
        originX: "start",
        originY: "top",
        overlayX: "start",
        overlayY: "bottom",
        offsetY: -gap,
      },
    ];
  }
}
